'use client'

import {
    ICON_BUTTON_ARIA_LABEL_MESSAGE_KEYS,
    resolveIconButtonAriaLabel,
} from '../../../constants/iconButtonAriaLabelMessageKeys'
import {
    Button,
    ButtonVariants,
    Retain,
    RetainBehaviorModifier,
    RetainSizeModifier,
    Text,
    Tooltip,
} from '../../atoms'
import { TextTypes } from '../../../types'
import { ArrowLeftIcon } from '../../icons'
import { DictionaryContext } from '../../../providers'
import React, { ReactElement, useContext } from 'react'

export enum EditStateVariant {
    DEFAULT = 'default',
    WARNING = 'warning',
}

export interface PageHeaderProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
    title: string | React.ReactNode
    description?: string | React.ReactNode
    buttons?: ReactElement
    contentSwitcher?: ReactElement
    retainSizeModifier?: RetainSizeModifier
    retainBehaviorModifiers?: RetainBehaviorModifier[]
    tooltipText?: string
    tag?: ReactElement
    previousHandler?: () => void
    leftButtons?: ReactElement
    isInEditState?: boolean
    editStateVariant?: EditStateVariant
}

export const PageHeader = ({
    title,
    description,
    buttons,
    contentSwitcher,
    retainSizeModifier = RetainSizeModifier.FULL_WIDTH,
    retainBehaviorModifiers = [RetainBehaviorModifier.NO_PADDING],
    tooltipText,
    tag,
    leftButtons,
    previousHandler,
    isInEditState = false,
    editStateVariant = EditStateVariant.DEFAULT,
    ...props
}: PageHeaderProps): ReactElement => {
    const dictionary = useContext(DictionaryContext)

    return (
        <div {...props} className={`sticky top-0 z-10 ${props.className ?? ''}`}>
            <div
                className={`h-full w-full${
                    isInEditState && editStateVariant === EditStateVariant.WARNING && ' bg-warning-100'
                }
             ${isInEditState && editStateVariant === EditStateVariant.DEFAULT && ' bg-accent-100'}
              ${!isInEditState && ' bg-secondary'}`}
            >
                <Retain sizeModifier={retainSizeModifier} behaviorModifiers={retainBehaviorModifiers}>
                    <div className="relative z-10 py-4">
                        {(previousHandler || leftButtons) && !isInEditState && (
                            <div className="absolute -translate-x-full top-1/2 -translate-y-1/2 -left-4">
                                {leftButtons ? leftButtons : undefined}
                                {previousHandler ? (
                                    <Button
                                        data-testid="back-button"
                                        variant={ButtonVariants.QUIET}
                                        icon={<ArrowLeftIcon />}
                                        onClick={() => previousHandler()}
                                        aria-label={resolveIconButtonAriaLabel(
                                            dictionary,
                                            ICON_BUTTON_ARIA_LABEL_MESSAGE_KEYS.NAVIGATE_BACK,
                                        )}
                                    />
                                ) : undefined}
                            </div>
                        )}
                        <div className="flex justify-between items-start gap-8">
                            <div className="inline-flex flex-col mr-auto self-center">
                                <div className="inline-flex items-center gap-2">
                                    <Text data-testid="page-header-title" type={TextTypes.HEADING_1}>
                                        {title}
                                    </Text>
                                    {tooltipText && <Tooltip content={tooltipText} />}
                                    {tag && tag}
                                </div>
                                {description && <div className="mt-1 text-sm text-text-tertiary">{description}</div>}
                            </div>
                            {contentSwitcher && contentSwitcher}
                            {buttons && <div className="inline-flex gap-2">{buttons}</div>}
                        </div>
                    </div>
                </Retain>
            </div>
        </div>
    )
}
